/**
 * tyt-grid-rows.js
 *
 * Hides whatever does not fill a whole row in a fixed-count product band.
 * Written 2026-09-07, for Jay: "trim to full rows".
 *
 * WHY IT IS NEEDED NOW. The product grids moved to a fixed CARD WIDTH with a
 * variable COLUMN COUNT (`.tyt-grid--cards`, see tyt-margherita.css). Bands
 * that render a set number of products used to divide evenly because the count
 * was always 4; now the homepage's 8 is two tidy rows at four columns but
 * 3 + 3 + 2 at three, and the 4-card bands become 3 + 1.
 *
 * SCOPE. Only `[data-tyt-trim-rows]`. The COLLECTION grid deliberately has no
 * such attribute — it paginates, and a short last row is correct there. Search
 * results are excluded for a different reason: they are a complete answer to a
 * query, and hiding one to tidy a row would hide a result the customer asked
 * for.
 *
 * FIVE THINGS THAT ARE LOAD-BEARING:
 *
 * 1. THE CSS OWNS THE COLUMN COUNT. It is read back out of the computed
 *    `grid-template-columns`, never re-derived here. `auto-fill` reports its
 *    full track list even when every child is absolutely positioned or the
 *    grid is empty (measured in Chrome), so there is no second copy of the
 *    breakpoints to drift — the same principle tyt-grid-flow.js follows.
 *
 * 2. IT HIDES, IT DOES NOT DELETE. The column count changes as the window
 *    resizes, so a card hidden at three columns must come back at four.
 *    tyt-shuffle-grid.js can delete because its choice is made once; this
 *    cannot.
 *
 * 3. IT NEVER TOUCHES A CARD PAST THE TARGET. On a shuffle grid the section
 *    emits a no-JS CSS floor hiding every child past `limit`, and the pool
 *    behind it can be 24 cards. Clearing `display` indiscriminately would
 *    flash all 24 before the shuffle ran. So this only ever hides within
 *    [keep, target) and leaves everything past `target` to the CSS floor and
 *    the shuffle.
 *
 * 4. IT WATCHES FOR CONTENT RATHER THAN ASSUMING A SCRIPT ORDER. Two things
 *    change these grids after load: tyt-shuffle-grid.js deletes its extras,
 *    and <product-recommendations> replaces its own innerHTML once the
 *    recommendations fetch returns — at first paint that element is empty.
 *    A MutationObserver on childList catches both. Observing childList ONLY is
 *    what stops it looping: this script's own writes are inline `style`
 *    changes on the children, which are attribute mutations, not childList
 *    ones.
 *
 * 5. IT DEGRADES TO TODAY. With JavaScript off nothing runs and every rendered
 *    card shows, which is exactly the current behaviour.
 *
 * Inline `style.display` rather than the `hidden` attribute: `.trail-card`
 * carries `display: block`, and a class selector outranks the browser's
 * built-in `[hidden]` rule — the trap that left `btn.hidden = true` doing
 * nothing theme-wide (BUILD-LOG, 2026-09-05). tyt-collection-filter.js hides
 * the same way, so tyt-grid-flow.js already recognises the shape.
 */
(function () {
  'use strict';

  var MARK = 'data-tyt-row-hidden';

  function gridsOf(root) {
    return (root || document).querySelectorAll('[data-tyt-trim-rows]');
  }

  /* The CSS is the single source of truth for how many columns there are. */
  function columnCount(grid) {
    var tpl = window.getComputedStyle(grid).gridTemplateColumns;
    if (!tpl || tpl === 'none') return 1;
    var n = tpl.split(' ').filter(function (t) { return t.length > 0; }).length;
    return n > 0 ? n : 1;
  }

  function trim(grid) {
    var cards = Array.prototype.slice.call(grid.children);
    if (!cards.length) return;

    /* An explicit value is the number of products the band means to show; with
     * no value the target is simply whatever arrived (the recommendations
     * grid, which cannot know its count until the fetch lands). */
    var attr = parseInt(grid.getAttribute('data-tyt-trim-rows'), 10);
    var target = isNaN(attr) ? cards.length : Math.min(attr, cards.length);
    if (target < 1) return;

    var cols = columnCount(grid);

    /* Fewer products than columns cannot make a full row at all, so show them
     * all rather than hiding everything. */
    var keep = cols <= target ? Math.floor(target / cols) * cols : target;
    if (keep < 1) keep = target;

    for (var i = 0; i < target; i++) {
      var card = cards[i];
      if (i < keep) {
        if (card.hasAttribute(MARK)) {
          card.style.display = '';
          card.removeAttribute(MARK);
        }
      } else if (!card.hasAttribute(MARK)) {
        card.style.display = 'none';
        card.setAttribute(MARK, '');
      }
    }
    grid.setAttribute('data-tyt-rows-on', String(cols));

    /* tyt-grid-flow.js positions the VISIBLE cards absolutely, so a card this
     * function just hid or re-showed has to be laid out again. Calling back is
     * what makes the two independent of load order: shuffle -> (MutationObserver)
     * -> trim -> flow, and the same on every resize and recommendations fetch. */
    if (window.tytGridFlow) window.tytGridFlow.layout();
  }

  function trimAll(root) {
    var grids = gridsOf(root);
    for (var i = 0; i < grids.length; i++) trim(grids[i]);
  }

  function watch(root) {
    var grids = gridsOf(root);
    for (var i = 0; i < grids.length; i++) {
      var grid = grids[i];
      if (grid._tytRowsWatched) continue;
      grid._tytRowsWatched = true;
      /* childList only — see note 4. `records[r].target` is the observed grid,
       * so the callback needs no closure over the loop variable. */
      new MutationObserver(function (records) {
        for (var r = 0; r < records.length; r++) trim(records[r].target);
      }).observe(grid, { childList: true });
    }
    trimAll(root);
  }

  /* Fraunces changes how many lines a trail name takes, which changes nothing
   * about the column count — but the recommendations fetch and the shuffle can
   * both land after DOMContentLoaded, so run again once everything has settled. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { watch(); });
  } else {
    watch();
  }
  window.addEventListener('load', function () { trimAll(); });

  var t;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(function () { trimAll(); }, 120);
  });

  document.addEventListener('shopify:section:load', function (e) { watch(e.target); });

  window.tytGridRows = { trim: trimAll };
})();
